// frontend/src/study_material/StudyMaterialViewer.js
import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { API_BASE, authFetch } from '../services/api.js';

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const StudyMaterialViewer = ({ item, onClose }) => {
  const [fileBlob, setFileBlob]           = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading]              = useState(true);
  const [error, setError]                  = useState('');
  const [ready, setReady] = useState(false);
  const blobUrlRef                         = useRef(null);
  const [zoom, setZoom]                    = useState(1);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [linkUrl, setLinkUrl] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [isVideoMode, setIsVideoMode] = useState(false);
  const pageWidth = window.innerWidth;
   

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    loadFile();

    // Viewer is now on screen — tell Android so hardware back closes it
    if (window.Android && window.Android.setViewerOpen) {
      window.Android.setViewerOpen(true);
    }

    const isYoutube = item.item_type === 'youtube';
    if (isYoutube && window.Android && window.Android.enterVideoMode) {
      window.Android.enterVideoMode();
      setIsVideoMode(true);
    }

    // Listen for hardware back button from Android
    const handleAndroidBack = () => {
      onClose();
    };
    window.addEventListener('androidBack', handleAndroidBack);

    return () => {
      document.body.style.overflow = '';
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);

      if (isYoutube && window.Android && window.Android.exitVideoMode) {
        window.Android.exitVideoMode();
        setIsVideoMode(false);
      }

      window.removeEventListener('androidBack', handleAndroidBack);

      // Viewer gone from screen — release the back button
      if (window.Android && window.Android.setViewerOpen) {
        window.Android.setViewerOpen(false);
      }
    };
  }, []);

  
  const loadFile = async () => {
  setReady(false);
  try {
    if (item.item_type === 'youtube') {
      const res = await authFetch(`${API_BASE}/study-material/student/video/${item.item_id}`);
      const data = await res.json();
      if (res.ok) { setEmbedUrl(data.embed_url + '?rel=0&modestbranding=1'); setReady(true); }
      else setError('Failed to load video.');
      setLoading(false);
      return;
    }

    if (item.item_type === 'link') {
      const res = await authFetch(`${API_BASE}/study-material/student/link/${item.item_id}`);
      const data = await res.json();
      if (res.ok) {
        if (window.Android && window.Android.openUrl) {
          window.Android.openUrl(data.external_url);
          onClose();
        } else {
          setLinkUrl(data.external_url);
          setReady(true);
        }
      } else setError('Failed to load link.');
      setLoading(false);
      return;
    }

    if (item.item_type === 'html') {
      const res = await authFetch(`${API_BASE}/study-material/student/html/${item.item_id}`);
      const data = await res.json();
      if (res.ok) { setHtmlContent(data.html_content); setReady(true); }
      else setError('Failed to load content.');
      setLoading(false);
      return;
    }

    const res = await authFetch(`${API_BASE}/study-material/student/file/${item.item_id}`);
      if (!res.ok) { setError('Failed to load file.'); setLoading(false); return; }

      const blob = await res.blob();

      if (item.item_type === 'image') {
        
        // Use base64 instead of blob URL — required for Android WebView
        const reader = new FileReader();
        reader.onloadend = () => {
          
          setFileBlob(reader.result);
          setLoading(false);
        };
        reader.onerror = () => {
          setError('Failed to load image.');
          setLoading(false);
        };
        reader.readAsDataURL(blob);
      } else {
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setFileBlob(url);
        setLoading(false);
      }

    } catch {
      setError('Failed to load file.');
      setLoading(false);
    }
  };

  const allPagesReady = ready;

  return (
    <div style={{ position:'fixed', inset:0, background:'#525659', zIndex:9999, display:'flex', flexDirection:'column' }}>

      {/* Header — hidden in video mode */}
      {!isVideoMode && (
      <div style={{ background:'#111827', color:'#f1f5f9', padding:'0.5rem 1rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
        <span style={{ fontSize:'0.85rem', fontWeight:600 }}>{item.title}</span>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
          {item.item_type !== 'youtube' && item.item_type !== 'link' && <>
            <button onClick={() => setZoom(z => Math.max(0.5, z - 0.1))} style={{ background:'#374151', border:'none', color:'white', borderRadius:'4px', width:'1.75rem', height:'1.75rem', cursor:'pointer', fontSize:'1rem' }}>−</button>
            <span style={{ color:'#cbd5e1', fontSize:'0.75rem', minWidth:'2.5rem', textAlign:'center' }}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(3, z + 0.1))} style={{ background:'#374151', border:'none', color:'white', borderRadius:'4px', width:'1.75rem', height:'1.75rem', cursor:'pointer', fontSize:'1rem' }}>+</button>
          </>}
          <button onClick={onClose} style={{ background:'#374151', border:'none', color:'white', borderRadius:'50%', width:'1.75rem', height:'1.75rem', cursor:'pointer', fontSize:'0.85rem', marginLeft:'0.25rem' }}>✕</button>
        </div>
      </div>
      )}

      {/* Floating X button — only visible in video mode */}
      {isVideoMode && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            zIndex: 10000,
            background: 'rgba(0,0,0,0.6)',
            border: 'none',
            color: 'white',
            borderRadius: '50%',
            width: '2rem',
            height: '2rem',
            cursor: 'pointer',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ✕
        </button>
      )}

      {/* Content */}
      <div style={{ flex:1, position:'relative', overflow:'hidden' }}>

        {/* Spinner overlay */}
        {!allPagesReady && (
          <div style={{ position:'absolute', inset:0, background:'#525659', zIndex:10, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <p style={{ color:'#ccc', fontSize:'0.9rem' }}>
              {loading ? 'Loading…' : 'Rendering…'}
            </p>
          </div>
        )}

        {/* Scroll area */}
        <div style={{ position:'absolute', inset:0, overflowY:'auto', overflowX:'auto', WebkitOverflowScrolling:'touch' }}>

          {/* HTML content */}
          {htmlContent && item.item_type === 'html' && (
            <iframe
              srcDoc={`<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>* { box-sizing: border-box; } body { margin: 12px; font-family: Arial, sans-serif; font-size: 15px; line-height: 1.6; color: #1e293b; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none; } table { border-collapse: collapse; width: 100%; } th, td { border: 1px solid #ccc; padding: 8px; text-align: left; } img { max-width: 100%; height: auto; pointer-events: none; } * { max-width: 100%; }</style></head><body oncontextmenu="return false;" oncopy="return false;" oncut="return false;"></head><body>${htmlContent}</body></html>`}
              style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
              sandbox="allow-same-origin"
              title={item.title}
            />
          )}

          {/* External Link — iframe */}
          {linkUrl && item.item_type === 'link' && (
            <iframe
              src={linkUrl}
              style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
              title={item.title}
              sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation"
            />
          )}

          {/* YouTube — outside scale div, fills container */}
          {embedUrl && item.item_type === 'youtube' && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', background:'#000' }}>
              <div style={{ width:'100%', maxWidth:'900px', aspectRatio:'16/9' }}>
                <iframe
                  src={embedUrl}
                  title={item.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                  style={{ width:'100%', height:'100%', border:'none' }}
                />
              </div>
            </div>
          )}

          {/* PDF and Image — inside scale div */}
          {item.item_type !== 'youtube' && item.item_type !== 'link' && (
            <div style={{ transform:`scale(${zoom})`, transformOrigin:'top center' }}>

              {error && (
                <p style={{ color:'#fca5a5', textAlign:'center', marginTop:'2rem' }}>{error}</p>
              )}

              {fileBlob && item.item_type === 'image' && (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', padding:'0.5rem' }}>
                  <img
                    src={fileBlob}
                    alt={item.title}
                    draggable="false"
                    onContextMenu={e => e.preventDefault()}
                    onLoad={() => setReady(true)}
                    style={{ width: pageWidth, maxWidth:'100%', height:'auto', objectFit:'contain', display:'block' }}
                  />
                </div>
              )}

              {fileBlob && item.item_type === 'pdf' && (
                <Document
                  file={fileBlob}
                  onLoadSuccess={({ numPages }) => { setNumPages(numPages); setReady(true); }}
                  onLoadError={() => setError('Failed to render PDF.')}
                  loading={null}
                >
                  {numPages && Array.from({ length: numPages }, (_, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'center', marginBottom:'0.5rem' }}>
                      <Page
                        pageNumber={i + 1}
                        width={pageWidth}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={null}
                        
                      />
                    </div>
                  ))}
                </Document>
              )}

            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default StudyMaterialViewer;